import React, { useState, useEffect } from 'react';

interface RecycleItem {
  id: number;
  name: string;
  type: string;
  size: string;
  content: string;
  created_at: string;
}

const RecycleBinManager: React.FC = () => {
  const [items, setItems] = useState<RecycleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    size: '',
    content: ''
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await fetch('/api/desktop/recycle');
      const data = await response.json();
      setItems(data.items);
      setLoading(false);
    } catch (err) {
      setError('Failed to load recycle bin items');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name || !formData.type || !formData.size) {
      setError('Name, type, and size are required');
      return;
    }

    try {
      const url = editingId ? `/api/desktop/recycle/${editingId}` : '/api/desktop/recycle';
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to save item');
      }

      setSuccess(editingId ? 'Item updated successfully' : 'Item added successfully');
      setFormData({ name: '', type: '', size: '', content: '' });
      setEditingId(null);
      fetchItems();
    } catch (err) {
      setError('Failed to save item');
    }
  };

  const handleEdit = (item: RecycleItem) => {
    setFormData({
      name: item.name,
      type: item.type,
      size: item.size,
      content: item.content || ''
    });
    setEditingId(item.id);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const response = await fetch(`/api/desktop/recycle/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete item');
      }

      setSuccess('Item deleted successfully');
      fetchItems();
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  const handleCancel = () => {
    setFormData({ name: '', type: '', size: '', content: '' });
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="recycle-manager">
      <h3>{editingId ? 'Edit Recycle Bin Item' : 'Add Recycle Bin Item'}</h3>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <form onSubmit={handleSubmit} className="window" style={{ padding: '15px', marginBottom: '20px' }}>
        <div className="field-row-stacked">
          <label htmlFor="item-name">Filename *</label>
          <input
            id="item-name"
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., document.txt"
            required
          />
        </div>

        <div className="field-row-stacked">
          <label htmlFor="item-type">File Type *</label>
          <input
            id="item-type"
            type="text"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            placeholder="e.g., Text Document"
            required
          />
        </div>

        <div className="field-row-stacked">
          <label htmlFor="item-size">File Size *</label>
          <input
            id="item-size"
            type="text"
            value={formData.size}
            onChange={(e) => setFormData({ ...formData, size: e.target.value })}
            placeholder="e.g., 5 KB"
            required
          />
        </div>

        <div className="field-row-stacked">
          <label htmlFor="item-content">Content</label>
          <textarea
            id="item-content"
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            placeholder="File contents (optional)"
            rows={4}
          />
        </div>

        <div className="field-row" style={{ justifyContent: 'flex-start', gap: '10px', marginTop: '10px' }}>
          <button type="submit">{editingId ? 'Update Item' : 'Add Item'}</button>
          {editingId && (
            <button type="button" onClick={handleCancel}>Cancel</button>
          )}
        </div>
      </form>

      <h3>Current Recycle Bin Items</h3>
      <div className="window" style={{ padding: '15px' }}>
        {items.length === 0 ? (
          <p>No items in recycle bin</p>
        ) : (
          <table className="interactive" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Type</th>
                <th>Size</th>
                <th>Content Preview</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.type}</td>
                  <td>{item.size}</td>
                  <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.content ? item.content.substring(0, 50) + (item.content.length > 50 ? '...' : '') : '-'}
                  </td>
                  <td>
                    <button onClick={() => handleEdit(item)} style={{ marginRight: '5px' }}>Edit</button>
                    <button onClick={() => handleDelete(item.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RecycleBinManager;
